import React, { useState } from 'react';
import { generatePDF } from './pdfGenerator';

interface ProjectData {
  projectName: string;
  estimatedHours: number;
  experienceLevel: 'Junior' | 'Mid' | 'Senior';
  projectScope: 'Yerel' | 'Global';
  projectComplexity: 'Düşük' | 'Orta' | 'Yüksek';
  additionalCosts: number;
  vatOption: 'KDV\'siz' | 'KDV Dahil';
  isRegularCustomer: boolean;
  isUrgent: boolean;
}

const FeeCalculator: React.FC = () => {
  const [formData, setFormData] = useState<ProjectData>({
    projectName: '',
    estimatedHours: 0,
    experienceLevel: 'Junior',
    projectScope: 'Yerel',
    projectComplexity: 'Orta',
    additionalCosts: 0,
    vatOption: 'KDV\'siz',
    isRegularCustomer: false,
    isUrgent: false
  });

  const [errors, setErrors] = useState({
    projectName: '',
    estimatedHours: ''
  });

  const baseRates = {
    Junior: 250,
    Mid: 500,
    Senior: 750
  };

  const complexityMultipliers = {
    'Düşük': 1,
    'Orta': 1.5,
    'Yüksek': 2
  };

  const calculateTotal = () => {
    const baseRate = baseRates[formData.experienceLevel];
    const complexityMultiplier = complexityMultipliers[formData.projectComplexity];
    const scopeMultiplier = formData.projectScope === 'Global' ? 1.5 : 1;
    const regularCustomerDiscount = formData.isRegularCustomer ? 0.9 : 1;
    const urgentFee = formData.isUrgent ? 1.25 : 1;

    let total = formData.estimatedHours * baseRate * complexityMultiplier * scopeMultiplier * regularCustomerDiscount * urgentFee;
    total += formData.additionalCosts;

    if (formData.vatOption === 'KDV Dahil') {
      total *= 1.18; // %18 KDV
    }

    return Math.round(total);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const newErrors = {
      projectName: '',
      estimatedHours: ''
    };

    if (!formData.projectName.trim()) {
      newErrors.projectName = 'Proje adı gerekli';
    }

    if (!formData.estimatedHours || formData.estimatedHours <= 0) {
      newErrors.estimatedHours = 'Geçerli bir süre giriniz';
    }

    setErrors(newErrors);
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-blue-700">KODLASA</h1>
        </div>
        <form onSubmit={handleSubmit} className="bg-white shadow-lg rounded-lg p-8 space-y-6">
          {/* Proje Adı */}
          <div>
            <label htmlFor="projectName" className="block text-sm font-medium text-gray-700">
              Proje Adı
            </label>
            <input
              type="text"
              id="projectName"
              value={formData.projectName}
              onChange={(e) => setFormData({...formData, projectName: e.target.value})}
              className="mt-1 block w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-700 focus:border-blue-700"
            />
            {errors.projectName && (
              <p className="mt-1 text-sm text-red-600">{errors.projectName}</p>
            )}
          </div>

          {/* Tahmini Süre */}
          <div>
            <label htmlFor="estimatedHours" className="block text-sm font-medium text-gray-700">
              Tahmini Süre (Saat)
            </label>
            <input
              type="number"
              id="estimatedHours"
              value={formData.estimatedHours || ''}
              onChange={(e) => setFormData({...formData, estimatedHours: parseInt(e.target.value) || 0})}
              className="mt-1 block w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-700 focus:border-blue-700"
            />
            {errors.estimatedHours && (
              <p className="mt-1 text-sm text-red-600">{errors.estimatedHours}</p>
            )}
          </div>

          {/* Tecrübe Seviyesi */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tecrübe Seviyesi
            </label>
            <div className="grid grid-cols-3 gap-4">
              {['Junior', 'Mid', 'Senior'].map((level) => (
                <button
                  key={level}
                  type="button"
                  onClick={() => setFormData({...formData, experienceLevel: level as any})}
                  className={`py-2 px-4 rounded-lg text-center ${
                    formData.experienceLevel === level
                      ? 'bg-blue-700 text-white'
                      : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {level}
                </button>
              ))}
            </div>
          </div>

          {/* Proje Kapsamı */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Proje Kapsamı
            </label>
            <div className="grid grid-cols-2 gap-4">
              {['Yerel', 'Global'].map((scope) => (
                <button
                  key={scope}
                  type="button"
                  onClick={() => setFormData({...formData, projectScope: scope as any})}
                  className={`py-2 px-4 rounded-lg text-center ${
                    formData.projectScope === scope
                      ? 'bg-blue-700 text-white'
                      : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {scope}
                </button>
              ))}
            </div>
          </div>

          {/* Proje Zorluğu */}
          <div>
            <label htmlFor="projectComplexity" className="block text-sm font-medium text-gray-700">
              Proje Zorluğu
            </label>
            <select
              id="projectComplexity"
              value={formData.projectComplexity}
              onChange={(e) => setFormData({...formData, projectComplexity: e.target.value as any})}
              className="mt-1 block w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-700 focus:border-blue-700"
            >
              <option value="Düşük">Düşük</option>
              <option value="Orta">Orta</option>
              <option value="Yüksek">Yüksek</option>
            </select>
          </div>

          {/* Ek Masraflar */}
          <div>
            <label htmlFor="additionalCosts" className="block text-sm font-medium text-gray-700">
              Ek Masraflar (TL)
            </label>
            <input
              type="number"
              id="additionalCosts"
              value={formData.additionalCosts || ''}
              onChange={(e) => setFormData({...formData, additionalCosts: parseInt(e.target.value) || 0})}
              className="mt-1 block w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-700 focus:border-blue-700"
            />
          </div>

          {/* KDV Seçeneği */}
          <div>
            <label htmlFor="vatOption" className="block text-sm font-medium text-gray-700">
              KDV
            </label>
            <select
              id="vatOption"
              value={formData.vatOption}
              onChange={(e) => setFormData({...formData, vatOption: e.target.value as any})}
              className="mt-1 block w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-700 focus:border-blue-700"
            >
              <option value="KDV'siz">KDV'siz</option>
              <option value="KDV Dahil">KDV Dahil</option>
            </select>
          </div>

          {/* Ek Seçenekler */}
          <div className="space-y-4">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="isRegularCustomer"
                checked={formData.isRegularCustomer}
                onChange={(e) => setFormData({...formData, isRegularCustomer: e.target.checked})}
                className="h-4 w-4 text-blue-700 focus:ring-blue-700 border-gray-300 rounded"
              />
              <label htmlFor="isRegularCustomer" className="ml-2 block text-sm text-gray-700">
                Sürekli Müşteri
              </label>
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                id="isUrgent"
                checked={formData.isUrgent}
                onChange={(e) => setFormData({...formData, isUrgent: e.target.checked})}
                className="h-4 w-4 text-blue-700 focus:ring-blue-700 border-gray-300 rounded"
              />
              <label htmlFor="isUrgent" className="ml-2 block text-sm text-gray-700">
                Acil İş
              </label>
            </div>
          </div>

          {/* Butonlar */}
          <div className="flex gap-4">
            <button
              type="submit"
              className="w-full bg-blue-700 text-white py-3 px-4 rounded-lg hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-700 transition-colors duration-150"
            >
              Hesapla
            </button>
          </div>

          {/* Toplam */}
          {formData.projectName && formData.estimatedHours > 0 && (
            <div className="pt-4 border-t border-gray-200">
              <div className="text-2xl font-bold text-gray-900 text-center">
                Toplam: {calculateTotal().toLocaleString('tr-TR')} ₺
                {formData.vatOption === 'KDV\'siz' && (
                  <span className="text-sm font-normal text-gray-500 block">
                    +KDV
                  </span>
                )}
              </div>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default FeeCalculator; 